/**
 * TranslationService - Manages language switching, translation loading, and text translation
 * with localStorage persistence and fallback logic
 */
class TranslationService {
  constructor() {
    this.currentLanguage = 'en';
    this.translations = {};
    this.supportedLanguages = {
      en: {
        code: 'en',
        name: 'English',
        flag: '🇨🇦',
        file: '/locales/en.json'
      },
      kr: {
        code: 'kr',
        name: '한국어',
        flag: '🇰🇷',
        file: '/locales/kr.json'
      }
    };
    this.isLoading = false;
    this.loadingPromises = new Map();
    this.isInitialized = false;
    this.initializationPromise = null;
    
    // Initialize with saved language preference or default to English
    this.initializationPromise = this.initializeLanguage();
  }

  /**
   * Initialize the translation service with saved language preference
   */
  async initializeLanguage() {
    try {
      const savedLanguage = localStorage.getItem('selectedLanguage');
      if (savedLanguage && this.supportedLanguages[savedLanguage]) {
        this.currentLanguage = savedLanguage;
      }
    } catch (error) {
      console.warn('Failed to load language preference from localStorage:', error);
      this.currentLanguage = 'en';
    }
    
    // Load the initial language file
    try {
      await this.loadLanguage(this.currentLanguage);
    } catch (error) {
      console.error('Failed to load initial language file:', error);
      // Fallback to English if the saved language fails to load
      if (this.currentLanguage !== 'en') {
        this.currentLanguage = 'en';
        try {
          await this.loadLanguage('en');
        } catch (fallbackError) {
          console.error('Failed to load English fallback:', fallbackError);
        }
      }
    }
    
    this.isInitialized = true;
  }

  /**
   * Load language file for the specified language
   * @param {string} language - Language code (en, kr)
   * @returns {Promise<Object>} Promise that resolves to the loaded translations
   */
  async loadLanguage(language) {
    if (!this.supportedLanguages[language]) {
      console.error(`Unsupported language: ${language}`);
      return this.translations[language] || {};
    }

    // Return cached translations if already loaded
    if (this.translations[language]) {
      return this.translations[language];
    }

    // Return existing loading promise if already in progress
    if (this.loadingPromises.has(language)) {
      return this.loadingPromises.get(language);
    }

    const loadingPromise = this._fetchLanguageFile(language);
    this.loadingPromises.set(language, loadingPromise);

    try {
      const translations = await loadingPromise;
      this.translations[language] = translations;
      return translations;
    } catch (error) {
      console.error(`Failed to load language file for ${language}:`, error);
      // Return empty object as fallback
      this.translations[language] = {};
      return {};
    } finally {
      this.loadingPromises.delete(language);
    }
  }

  /**
   * Fetch language file from server
   * @param {string} language - Language code
   * @returns {Promise<Object>} Promise that resolves to translations object
   * @private
   */
  async _fetchLanguageFile(language) {
    const languageConfig = this.supportedLanguages[language];
    
    try {
      const response = await fetch(languageConfig.file);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const translations = await response.json();
      
      if (typeof translations !== 'object' || translations === null) {
        throw new Error('Invalid translation file format');
      }
      
      return translations;
    } catch (error) {
      console.error(`Error fetching language file ${languageConfig.file}:`, error);
      throw error;
    }
  }

  /**
   * Translate a key to the current language with fallback logic
   * @param {string} key - Translation key (e.g., 'nav.home')
   * @param {Object} params - Optional parameters for string interpolation
   * @returns {string} Translated text or fallback
   */
  translate(key, params = {}) {
    if (!key || typeof key !== 'string') {
      console.warn('Invalid translation key:', key);
      return key || '';
    }

    // If not initialized yet, return the key as fallback
    // This prevents showing undefined during initial load
    if (!this.isInitialized) {
      return key;
    }

    // Try to get translation from current language
    let translation = this._getNestedValue(this.translations[this.currentLanguage], key);
    
    // Fallback to English if not found and current language is not English
    if (translation === undefined && this.currentLanguage !== 'en') {
      translation = this._getNestedValue(this.translations['en'], key);
      
      if (translation !== undefined) {
        console.warn(`Translation missing for key "${key}" in language "${this.currentLanguage}", using English fallback`);
      }
    }
    
    // Final fallback to the key itself
    if (translation === undefined) {
      console.warn(`Translation missing for key "${key}" in all languages`);
      return key;
    }

    // Handle string interpolation if params provided
    if (Object.keys(params).length > 0) {
      return this._interpolateString(translation, params);
    }

    return translation;
  }

  /**
   * Get nested value from object using dot notation
   * @param {Object} obj - Object to search in
   * @param {string} key - Dot-separated key path
   * @returns {*} Value or undefined if not found
   * @private
   */
  _getNestedValue(obj, key) {
    if (!obj || typeof obj !== 'object') {
      return undefined;
    }

    return key.split('.').reduce((current, keyPart) => {
      return current && current[keyPart] !== undefined ? current[keyPart] : undefined;
    }, obj);
  }

  /**
   * Interpolate parameters into a string
   * @param {string} template - Template string with {{param}} placeholders
   * @param {Object} params - Parameters to interpolate
   * @returns {string} Interpolated string
   * @private
   */
  _interpolateString(template, params) {
    return template.replace(/\{\{(\w+)\}\}/g, (match, paramName) => {
      return params[paramName] !== undefined ? params[paramName] : match;
    });
  }

  /**
   * Set the current language and persist to localStorage
   * @param {string} language - Language code to set
   * @returns {Promise<boolean>} Promise that resolves to true if successful
   */
  async setLanguage(language) {
    if (!this.supportedLanguages[language]) {
      console.error(`Cannot set unsupported language: ${language}`);
      return false;
    }

    try {
      // Load the language file if not already loaded
      await this.loadLanguage(language);
      
      // Update current language
      this.currentLanguage = language;
      
      // Persist to localStorage
      try {
        localStorage.setItem('selectedLanguage', language);
      } catch (error) {
        console.warn('Failed to save language preference to localStorage:', error);
      }
      
      return true;
    } catch (error) {
      console.error(`Failed to set language to ${language}:`, error);
      return false;
    }
  }

  /**
   * Get the current language code
   * @returns {string} Current language code
   */
  getCurrentLanguage() {
    return this.currentLanguage;
  }

  /**
   * Get all supported languages
   * @returns {Object} Supported languages configuration
   */
  getSupportedLanguages() {
    return { ...this.supportedLanguages };
  }

  /**
   * Check if a language is currently loading
   * @param {string} language - Language code to check
   * @returns {boolean} True if language is loading
   */
  isLanguageLoading(language) {
    return this.loadingPromises.has(language);
  }

  /**
   * Check if a language is loaded
   * @param {string} language - Language code to check
   * @returns {boolean} True if language is loaded
   */
  isLanguageLoaded(language) {
    return this.translations[language] !== undefined;
  }

  /**
   * Preload a language file
   * @param {string} language - Language code to preload
   * @returns {Promise<boolean>} Promise that resolves to true if successful
   */
  async preloadLanguage(language) {
    try {
      await this.loadLanguage(language);
      return true;
    } catch (error) {
      console.error(`Failed to preload language ${language}:`, error);
      return false;
    }
  }

  /**
   * Wait for the translation service to be initialized
   * @returns {Promise<void>} Promise that resolves when initialization is complete
   */
  async waitForInitialization() {
    if (this.isInitialized) {
      return;
    }
    
    if (this.initializationPromise) {
      await this.initializationPromise;
    }
  }

  /**
   * Check if the translation service is initialized
   * @returns {boolean} True if initialized
   */
  isServiceInitialized() {
    return this.isInitialized;
  }

  /**
   * Clear all cached translations (useful for testing or memory management)
   */
  clearCache() {
    this.translations = {};
    this.loadingPromises.clear();
  }
}

// Create and export singleton instance
const translationService = new TranslationService();

export default translationService;